import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

import StoryEditorSidebar from "../StoryEditor/Sidebar";
import ContentContainer from "modules/ContentContainer";
import { kioskManagerMapStateToProps } from "mapToProps/kioskManager";
import * as kioskActions from "actions/kiosks";
import "./KioskManager.scss";
import { PublishStatusDialog } from "./PublishStatusDialog";
import SystemReport from "./SystemReport";
import { PublishManager } from "./PublishManager";
import { PublishHistory2 } from "./PublishHistory2";

export class KioskManager extends Component {
  constructor(props) {
    super(props);

      this.state = {
        isPublishStatusModalOpen: false,
        selectedPublishId: null,
        // When true, the dialog is open but waiting for an in-progress publish
        // entry to appear in the data so it can lock onto the correct publishId.
        awaitingPublishId: false
      };

    // Poll control for kiosk data refresh
    this._kioskDataInterval = null;

    this.handlePublishCancelClick = this.handlePublishCancelClick.bind(this);
    this.handleViewDetailsClick = this.handleViewDetailsClick.bind(this);
    this.handleViewDetailsClickWithId = this.handleViewDetailsClickWithId.bind(this);
    this.startUptimePolling = this.startUptimePolling.bind(this);
    this.stopUptimePolling = this.stopUptimePolling.bind(this);
    this.handlePublishStatusDialogClose =
      this.handlePublishStatusDialogClose.bind(this);
  }

  componentDidMount() {
    this.props.requestKiosk();
    this.props.requestProjects();
    this.props.requestKioskAppVersions();
    this.startUptimePolling();
  }

  componentDidUpdate(prevProps, prevState) {
    /*
    If kioskRevertCopy is undefined, this screen has not created a revert snapshot yet.
    When kioskData is defined, the store is populated and a snapshot can be stored.

    If kioskRevertCopy exists but its _id does not match the route kioskID, the user
    navigated to a different kiosk; create a fresh snapshot for the new kiosk.
    */
    if (typeof this.props.kioskRevertCopy === "undefined") {
      if (typeof this.props.kioskData !== "undefined") {
        this.props.createKioskRevertCopy(this.props.kioskID);
      }
    } else {
      if (this.props.kioskID !== this.props.kioskRevertCopy._id) {
        this.props.createKioskRevertCopy(this.props.kioskID);
      }
    }

    // When the dialog is open awaiting a publishId, scan from the end of
    // publishHistory for the most recent in-progress entry. This avoids a
    // race condition where the data may already contain the new publish by
    // the time the user clicks "View details."
    if (this.state.isPublishStatusModalOpen && !this.state.selectedPublishId && this.state.awaitingPublishId) {
      const { kioskData } = this.props;
      if (kioskData && kioskData.publishHistory && kioskData.publishHistory.length > 0) {
        const history = kioskData.publishHistory;
        for (let i = history.length - 1; i >= 0; i--) {
          const entry = history[i];
          const isComplete = entry.status === "complete";
          const isError = entry.metadata && entry.metadata.error;
          if (entry.publishId && !isComplete && !isError) {
            this.setState({ selectedPublishId: entry.publishId, awaitingPublishId: false });
            break;
          }
        }
      }
    }
  }

  componentWillUnmount() {
    this.stopUptimePolling();
  }

  handlePublishCancelClick() {
    const { kioskData, updatePublishHistoryEntry } = this.props;
    
    if (!kioskData) {
      console.warn("Cannot cancel publish: kiosk data not available");
      return;
    }

    // Get the current publish status
    const currentStatus = this.getCurrentPublishStatus();
    
    // Find the most recent publishId from publishHistory that matches the current status
    const publishHistory = kioskData.publishHistory || [];
    const currentEntry = publishHistory
      .filter(entry => entry.status === currentStatus)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

    if (!currentEntry || !currentEntry.publishId) {
      console.warn("Cannot cancel publish: no matching publish history entry found");
      return;
    }

    const metadata = {
      cancelledAt: new Date()
    };

    // Update the publish history entry
    updatePublishHistoryEntry(
      kioskData._id,
      currentEntry.publishId,
      currentEntry.stage,
      currentStatus,
      metadata
    );
  }

  startUptimePolling() {
    // Stop any existing polling first to avoid multiple intervals
    this.stopUptimePolling();

    // Poll kiosk record every 8 seconds (e.g. lastSeenTS / online state)
    this._kioskDataInterval = setInterval(() => {
      this.props.requestKiosk({ lastSeenTS: true });
    }, 8000);
  }

  stopUptimePolling() {
    if (this._kioskDataInterval) {
      clearInterval(this._kioskDataInterval);
      this._kioskDataInterval = null;
    }
  }

  handleViewDetailsClick = (isNewPublish) => {
    const { kioskData } = this.props;
    const publishHistory = kioskData && kioskData.publishHistory;

    // When a publish was just initiated, publishHistory may still contain the
    // previous publish's data. Open the dialog with no publishId and let
    // componentDidUpdate detect the first in-progress entry.
    if (isNewPublish) {
      this.setState({
        isPublishStatusModalOpen: true,
        selectedPublishId: null,
        awaitingPublishId: true
      });
      return;
    }

    const mostRecentPublishId = publishHistory && publishHistory.length > 0
      ? publishHistory[publishHistory.length - 1].publishId
      : null;
    if (!mostRecentPublishId) {
      console.warn("Cannot view details: no publish history entry found");
      return;
    }
    this.setState({
      isPublishStatusModalOpen: true,
      selectedPublishId: mostRecentPublishId,
      awaitingPublishId: false
    });
  };

  handleViewDetailsClickWithId = (publishId) => {
    this.setState({
      isPublishStatusModalOpen: true,
      selectedPublishId: publishId
    });
  };

  handlePublishStatusDialogClose = () => {
    this.setState({ 
      isPublishStatusModalOpen: false,
      selectedPublishId: null,
      awaitingPublishId: false
    });
  };

  /**
   * Gets the current publish status to display, resetting to "preparing" if a new publish was just initiated
   * @returns {String} The publish status to display
   */
  getCurrentPublishStatus() {
    const { kioskData } = this.props;
    const { selectedPublishId, awaitingPublishId } = this.state;

    // Dialog is open but waiting for an in-progress entry to appear;
    // show "preparing" rather than the stale status from the previous publish.
    if (awaitingPublishId) {
      return "preparing";
    }

    if (selectedPublishId && kioskData && kioskData.publishHistory) {
      const publishHistory = kioskData.publishHistory;
      const entriesForPublishId = publishHistory.filter(entry => entry.publishId === selectedPublishId);
      if (entriesForPublishId.length > 0) {
        const sortedEntries = entriesForPublishId.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        return sortedEntries[sortedEntries.length - 1].status;
      }
    }

    return kioskData ? kioskData.publishStatus : "preparing";
  }

  /**
   * Gets the publish history entries for a specific publishId
   * @param {String} publishId - The publishId to get history for
   * @returns {Array} Array of history entries for the publishId
   */
  getPublishHistoryForId(publishId) {
    const { kioskData } = this.props;
    if (!publishId || !kioskData || !kioskData.publishHistory) {
      return [];
    }
    return kioskData.publishHistory
      .filter(entry => entry.publishId === publishId)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }

  render() {
    const {
      kioskData,
      section,
      userPreferences,
      userPreferencesSelector,
      sidebarProps,
      sidebarMode,
      kioskType,
      settings,
      allProjects
    } = this.props;

    const SidebarActionBar = (
      <div>
        <div className="story-type-container">
          {kioskData ? kioskData.title : ""}
        </div>
      </div>
    );

    let sidebarActions = null;
    if (section === "settings") {
      sidebarActions = {
        handleInputChange: (prop, value, isLocalized) =>
          this.props.handleSettingsInputChange(prop, value, isLocalized),
        handleSave: () => {
          this.props.createKioskRevertCopy();
          this.props.saveKiosk("settings");
        },
        handleRevert: () => this.props.revertKioskChanges(),
        changeLanguage: value => this.props.changeLanguage(value),
        languages: this.props.languages,
        langID: this.props.langID,
        saveLabel: "Save",
        showButtons: true
      };
    }

    const topBar = (
      <div
        className={`status-indicator ${
          kioskData && kioskData.isOnline ? "online" : "offline"
        }`}
      >
        <div className="icon" />
        <div className="label">
          {kioskData && kioskData.isOnline ? "Online" : "Offline"}
        </div>
      </div>
    );

    let sidebarChildren;
    if (section === "systemReport") {
      sidebarChildren = null;
    } else if (section === "settings") {
      sidebarChildren = null;
    }

    const className =
      section === "systemReport"
        ? "kiosk-system-report"
        : section === "settings"
        ? "kiosk-settings"
        : "kiosk-manager";

    return (
      <div className={`cms-screen ${className}`}>
        <Helmet>
          <title>
            {section === undefined
              ? "Manage Kiosk"
              : section === "systemReport"
              ? "Kiosk: System Report"
              : section === "settings"
              ? "Kiosk: Settings"
              : "Kiosk: TBD"}
          </title>
        </Helmet>

        <StoryEditorSidebar
          {...sidebarProps}
          actionBar={SidebarActionBar}
          {...sidebarActions}
        >
          {sidebarChildren}
        </StoryEditorSidebar>

        <ContentContainer
          className="content-container"
          sidebarMode={sidebarMode}
          topBarAlignContentRight={false}
          topBar={topBar}
        >
          {section === "systemReport" ? (
            <SystemReport
              kioskData={kioskData}
              kioskType={kioskType}
              settings={settings}
            />
          ) :
          section === "settings" ? (
            null
          ) : (
            <div>
              <PublishManager
                projects={allProjects}
                kioskData={kioskData}
                handleSettingsInputChange={this.props.handleSettingsInputChange}
                saveKiosk={this.props.saveKiosk}
                publishKioskProject={this.props.publishKioskProject}
                requestKiosk={this.props.requestKiosk}
                handlePublishCancelClick={this.handlePublishCancelClick}
                handleViewDetailsClick={this.handleViewDetailsClick}
              />
              <PublishHistory2
                kioskData={kioskData}
                projects={allProjects}
                handleViewDetailsClick={this.handleViewDetailsClickWithId}
                onRevertClick={this.props.revertKioskBundle}
              />
            </div>
          )}
        </ContentContainer>

        <PublishStatusDialog
          isOpen={this.state.isPublishStatusModalOpen}
          publishStatus={this.getCurrentPublishStatus()}
          publishId={this.state.selectedPublishId}
          publishHistory={this.state.selectedPublishId ? this.getPublishHistoryForId(this.state.selectedPublishId) : null}
          onClose={this.handlePublishStatusDialogClose}
        />
      </div>
    );
  }
}

KioskManager.defaultProps = {
  isLoading: false
};

KioskManager.propTypes = {
  isLoading: PropTypes.bool
};

function mapStateToProps(state, ownProps) {
  return kioskManagerMapStateToProps(state, ownProps);
}

function mergeProps(stateProps, dispatchProps, ownProps) {
  const { kioskID, username } = stateProps;

  return Object.assign({}, stateProps, {
    requestProjects: () => dispatchProps.requestProjects(username),
    requestKiosk: filters =>
      dispatchProps.kioskFetchRequest(kioskID, filters || null),
    publishKioskProject: () => dispatchProps.kioskPublishRequest(kioskID),
    handleSettingsInputChange: (prop, value) => {
      dispatchProps.setKioskProp(kioskID, prop, value);
    },
    saveKiosk: saveType => dispatchProps.kioskUpdateRequest(kioskID, saveType),
    createKioskRevertCopy: () =>
      dispatchProps.createKioskRevertCopy(kioskID),
    revertKioskChanges: () => dispatchProps.revertKioskChanges(),
    updatePublishHistoryEntry: (
      targetKioskID,
      publishID,
      stage,
      status,
      metadata
    ) => {
      dispatchProps.kioskUpdateHistoryEntry(
        targetKioskID,
        publishID,
        stage,
        status,
        metadata
      );
    },
    revertKioskBundle: bundleID =>
      dispatchProps.kioskRevertRequest(kioskID, bundleID),
    requestKioskAppVersions: () =>
      dispatchProps.kioskAppVersionsFetchRequest()
  });
}

export default connect(
  mapStateToProps,
  kioskActions,
  mergeProps
)(KioskManager);
